import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { catchError, filter, Observable, switchMap, take, throwError } from "rxjs";
import { Injectable } from "@angular/core";
import { SessionService } from "./storage/session.service";
import { ApiService } from "./api.service";
import { IdentityServerGrantBase, IdentityServerRefresh } from "../models/auth";
import { BasePaths } from "../constants/routes";
import { ToastType } from "../constants/toast-type";
import { ToastService } from "./toast.service";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { IError } from "../models/error";

@Injectable()
export class APIInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private sessionService: SessionService,
    private toast: ToastService,
  ) { }

  intercept(httpRequest: HttpRequest<any>, httpHandler: HttpHandler): Observable<HttpEvent<any>> {
    return httpHandler.handle(this.addToken(httpRequest, this.apiService.apiStatus.authToken)).pipe(
      catchError(error => {
        let l_IsLogout: boolean;
        if (error instanceof HttpErrorResponse) {
          let errorMessage: IError = new IError();
          let l_ToastType: ToastType = ToastType.error;
          let l_IsErrorBlobType: boolean;

          switch (error.status) {
            case 401:
              return this.handle401Error(httpRequest, httpHandler, error);

            case 0:
              errorMessage.header = 'Connection Error';
              if (environment.production)
                errorMessage.description = 'Unable to connect to Server. Check your internet connection and please try again.';
              else
                errorMessage.description = 'API is not running! Check server or your local API build.';
              l_ToastType = ToastType.warning;
              break;
            case 403:
              errorMessage.header = 'Invalid Credentials';
              errorMessage.description = 'You do not have permission to view the selected page.';
              break;
            case 404:
              errorMessage.description = 'Server Offline! Please contact administrator of this issue.';
              break;
            case 405:
              errorMessage.description = 'Server Connection denied by server! Please contact administrator of this issue.';
              break;
            case 417:
              errorMessage.header = 'System Exception';
              errorMessage.description = 'Something went wrong with this page. Please contact administrator of this issue. Error Id: ' + error.error;
              break;
            case 424:
              errorMessage.header = 'Invalid Request';
              l_IsErrorBlobType = error.error instanceof Blob;
              if (!l_IsErrorBlobType) {
                errorMessage.description = error.error;
              }
              l_ToastType = ToastType.warning;
              break;
            default:
              errorMessage.description = 'Something went wrong. Please try again in a few minutes.';
              break;
          }

          if (l_IsErrorBlobType) {
            const message = error.error as Blob;
            message.text().then(value => {
              this.toast.buildToastMessage(l_ToastType, errorMessage.header, value);
            });
          } else {
            this.toast.buildToastMessage(l_ToastType, errorMessage.header, errorMessage.description);
          }
        }

        if (l_IsLogout) {
          const l_Path = this.sessionService.session.isAgent ? BasePaths.Counselors : BasePaths.Members;
          this.sessionService.logout();
          this.router.navigateByUrl(l_Path);
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(httpRequest: HttpRequest<any>, token: string, payload: any = null): HttpRequest<any> {
    if (!token)
      return httpRequest;

    if (payload)
      return httpRequest.clone({
        body: payload,
        setHeaders: { Authorization: `Bearer ${token}` }
      });

    return httpRequest.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  private handle401Error(httpRequest: HttpRequest<any>, httpHandler: HttpHandler, error: HttpErrorResponse) {
    if (this.sessionService.session.isLoggedIn && !this.apiService.requireTokenRefresh) {
      this.apiService.requireTokenRefresh = true;
      if (httpRequest.url.endsWith('/refresh-session'))
        this.apiService.isSessionTokenRefreshPath = true;
      this.apiService.$RefreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((tokenResponse: IdentityServerGrantBase) => {
          this.apiService.requireTokenRefresh = false;

          if (!tokenResponse.isSuccess) {
            this.toast.buildToastMessage(ToastType.error, 'Session Expired', 'Your session has expired or you are not authorized to continue. If you wish to resume working, please log in again.');
            this.apiService.setAuthToken(null, null);
            return throwError(() => error);
          }

          this.apiService.$RefreshTokenSubject.next(tokenResponse.accessToken);

          if (this.apiService.isSessionTokenRefreshPath) {
            this.apiService.isSessionTokenRefreshPath = false;
            const l_Payload = new IdentityServerRefresh();
            l_Payload.refreshToken = this.apiService.apiStatus.refreshToken;
            return httpHandler.handle(this.addToken(httpRequest, tokenResponse.accessToken, l_Payload));
          } else {
            return httpHandler.handle(this.addToken(httpRequest, tokenResponse.accessToken));
          }

        }),
        catchError(err => {
          this.apiService.requireTokenRefresh = false;
          this.toast.buildToastMessage(ToastType.error, 'Session Expired', 'Your session has expired or you are not authorized to continue. If you wish to resume working, please log in again.');
          return throwError(() => err);
        })
      );
    } else if (this.apiService.requireTokenRefresh) {
      // If refresh is already happening, queue requests until new token is ready
      return this.apiService.$RefreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => httpHandler.handle(this.addToken(httpRequest, token!)))
      );
    } else if (this.apiService?.apiStatus?.authToken) {
      this.apiService.setAuthToken(null, null);
      this.toast.buildToastMessage(ToastType.error, "Access Denied", error.error);
    } else {
      this.toast.buildToastMessage(ToastType.error, "Access Denied", error.error);
    }
    return throwError(() => error);
  }
}